import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CharacterBrowserGrid from '../components/CharacterBrowserGrid';
import { listUserCharacters } from '../services/characterService';
import { useAuth } from '../context/AuthContext';

/**
 * CharacterBrowser Component
 * Displays the character grid in the main content area
 * (Left panel is handled by the parent Chat layout)
 */
const CharacterBrowser = () => {
  const navigate = useNavigate();
  const { firebaseUser } = useAuth();

  const [predefinedCharacters, setPredefinedCharacters] = useState([]);
  const [customCharacters, setCustomCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCharacters();
  }, [firebaseUser]);

  const fetchCharacters = async () => {
    if (!firebaseUser) {
      setError('You must be logged in to view characters.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = await firebaseUser.getIdToken();
      
      // Fetch all characters (custom + predefined) from the browser endpoint
      const result = await listUserCharacters(token);
      
      if (result.success) {
        setCustomCharacters(result.data?.custom || []);
        setPredefinedCharacters(result.data?.predefined || []);
      } else {
        console.error('Failed to fetch characters:', result.error);
        setError(result.error || 'Failed to load characters');
      }
    } catch (err) {
      console.error('Error fetching characters:', err);
      setError('Failed to load characters. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading characters...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-2 text-lg font-medium text-gray-900">Error</h3>
          <p className="mt-1 text-gray-600">{error}</p>
          <button
            onClick={fetchCharacters}
            className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Merge into one sorted list: custom first, then interacted predefined, then rest
  const allCharacters = [
    ...customCharacters.map(c => ({ ...c, _type: 'custom' })),
    ...predefinedCharacters.map(c => ({ ...c, _type: 'predefined' }))
  ];

  return (
    <div className="w-full h-full flex flex-col overflow-y-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Choose a Character</h1>
          <p className="text-sm text-gray-600 mt-1">Start a conversation with your AI companion</p>
        </div>
        <CharacterBrowserGrid characters={allCharacters} />
      </div>
    </div>
  );
};

export default CharacterBrowser;
